import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSchedules, completeSchedule, deleteSchedule } from '../services/maintenanceService';
import { useLocationContext } from '../contexts/LocationContext';

const MaintenanceSchedules = () => {
  const { currentLocation } = useLocationContext();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completingId, setCompletingId] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    completed_date: new Date().toISOString().split('T')[0],
    parts_cost: '',
    labor_cost: '',
    performed_by: ''
  });

  useEffect(() => {
    fetchSchedules();
  }, [currentLocation?.id]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const data = await getSchedules();
      setSchedules(data.schedules);
    } catch (err) {
      setError('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (scheduleId) => {
    try {
      await completeSchedule(scheduleId, {
        completed_date: completeForm.completed_date,
        parts_cost: parseFloat(completeForm.parts_cost) || 0,
        labor_cost: parseFloat(completeForm.labor_cost) || 0,
        performed_by: completeForm.performed_by
      });
      setCompletingId(null);
      setCompleteForm({
        completed_date: new Date().toISOString().split('T')[0],
        parts_cost: '',
        labor_cost: '',
        performed_by: ''
      });
      fetchSchedules();
    } catch (err) {
      setError('Failed to mark schedule as done');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete schedule "${title}"? This won't delete existing work orders.`)) return;
    try {
      await deleteSchedule(id);
      fetchSchedules();
    } catch (err) {
      setError('Failed to delete schedule');
    }
  };

  const statusColor = (schedule) => {
    if (!schedule.active) return 'bg-gray-100 text-gray-500';
    if (schedule.is_overdue) return 'bg-red-50 border-l-4 border-red-400';
    if (schedule.days_until_due !== null && schedule.days_until_due <= 7) return 'bg-yellow-50 border-l-4 border-yellow-400';
    return 'bg-white border-l-4 border-green-400';
  };

  const statusBadge = (schedule) => {
    if (!schedule.active) return <span className="text-xs font-medium text-gray-400">Inactive</span>;
    if (schedule.is_overdue) return <span className="text-xs font-bold text-red-600">OVERDUE</span>;
    if (schedule.days_until_due !== null && schedule.days_until_due <= 7) return <span className="text-xs font-semibold text-yellow-600">Due Soon</span>;
    return <span className="text-xs font-medium text-green-600">On Track</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Maintenance Schedules</h1>
          <div className="hidden sm:flex space-x-3">
            <Link
              to="/maintenance"
              className="text-gray-600 hover:text-gray-800 text-sm font-medium px-4 py-2"
            >
              Back to Maintenance
            </Link>
            <Link
              to="/maintenance/schedule/new"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + New Schedule
            </Link>
          </div>
        </div>
        <div className="sm:hidden flex space-x-2 mt-3">
          <Link
            to="/maintenance/schedule/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          >
            + New Schedule
          </Link>
          <Link
            to="/maintenance"
            className="text-gray-600 hover:text-gray-800 text-xs font-medium px-3 py-1.5"
          >
            Back to Maintenance
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No maintenance schedules yet.{' '}
          <Link to="/maintenance/schedule/new" className="text-blue-600 hover:text-blue-800">
            Create your first schedule
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map(schedule => (
            <div key={schedule.id} className={`rounded-lg shadow p-4 ${statusColor(schedule)}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{schedule.title}</h3>
                    <span className="hidden sm:inline">{statusBadge(schedule)}</span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-0.5">
                    {(schedule.equipment_category?.length > 0 || schedule.equipment_area) && (
                      <p>Equipment: <span className="font-medium">{
                        schedule.equipment_category?.length > 0
                          ? schedule.equipment_category.join(', ')
                          : schedule.equipment_area?.name
                      }</span></p>
                    )}
                    <p>Every <span className="font-medium">{schedule.interval_days}</span> days</p>
                    <p>
                      Next due: <span className="font-medium">{formatDate(schedule.next_due_date)}</span>
                      {schedule.days_until_due !== null && (
                        <span className={`ml-2 ${schedule.is_overdue ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                          ({schedule.is_overdue ? `${Math.abs(schedule.days_until_due)} days overdue` : `${schedule.days_until_due} days`})
                        </span>
                      )}
                    </p>
                    {schedule.last_completed_date && (
                      <p>Last completed: <span className="font-medium">{formatDate(schedule.last_completed_date)}</span></p>
                    )}
                    {schedule.description && (
                      <p className="text-gray-500 mt-1">{schedule.description}</p>
                    )}
                  </div>
                </div>
                {/* Desktop buttons — inline right */}
                <div className="hidden sm:flex items-start space-x-2 ml-4">
                  {schedule.active && completingId !== schedule.id && (
                    <button
                      onClick={() => setCompletingId(schedule.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      Mark Done
                    </button>
                  )}
                  <Link
                    to={`/maintenance/schedule/edit/${schedule.id}`}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(schedule.id, schedule.title)}
                    className="text-red-600 hover:text-red-800 px-2 py-1.5 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {/* Mobile buttons — below text */}
              <div className="sm:hidden flex space-x-2 mt-3">
                {schedule.active && completingId !== schedule.id && (
                  <button
                    onClick={() => setCompletingId(schedule.id)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  >
                    Mark Done
                  </button>
                )}
                <Link
                  to={`/maintenance/schedule/edit/${schedule.id}`}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  Edit
                </Link>
                <button
                  onClick={() => handleDelete(schedule.id, schedule.title)}
                  className="text-red-600 hover:text-red-800 px-2 py-1.5 text-xs"
                >
                  Delete
                </button>
              </div>

              {/* Inline completion form */}
              {completingId === schedule.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Complete this maintenance</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Date</label>
                      <input
                        type="date"
                        value={completeForm.completed_date}
                        onChange={(e) => setCompleteForm(prev => ({ ...prev, completed_date: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Parts Cost ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={completeForm.parts_cost}
                        onChange={(e) => setCompleteForm(prev => ({ ...prev, parts_cost: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Labor Cost ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={completeForm.labor_cost}
                        onChange={(e) => setCompleteForm(prev => ({ ...prev, labor_cost: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Performed By</label>
                      <input
                        type="text"
                        value={completeForm.performed_by}
                        onChange={(e) => setCompleteForm(prev => ({ ...prev, performed_by: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Name"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => handleComplete(schedule.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded text-sm font-medium transition-colors"
                    >
                      Confirm Done
                    </button>
                    <button
                      onClick={() => setCompletingId(null)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1.5 rounded text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaintenanceSchedules;
